// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
import { LOGISTICS_EXPORT_GATES, LOGISTICS_EXPORT_KINDS, } from '../types/logistics.js';
import { CTA_EXPORT_KINDS } from '../types/customs-tax-administration.js';
import { MANUFACTURING_CAPA_COLLECTION, MANUFACTURING_EXPORT_KINDS, MANUFACTURING_PRODUCT_COLLECTION, } from '../types/manufacturing.js';
const NONE = { overlay: 'none' };
const ALWAYS = { overlay: 'always' };
/**
 * A `by_kind` class from the owner type's known kinds (its typed kind union, as
 * the module declares it at run time) and the overlay kinds among them. The
 * shared kinds are the rest — an allow-list, so a kind the union does not know
 * is withheld rather than shown.
 */
function byKind(known, overlayKinds) {
    return {
        overlay: 'by_kind',
        kinds: overlayKinds,
        shared_kinds: known.filter((k) => !overlayKinds.includes(k)),
    };
}
/** The logistics kinds an export of `ownerType` is stored under (`LOGISTICS_EXPORT_GATES`). */
const logisticsKindsOf = (ownerType) => LOGISTICS_EXPORT_KINDS.filter((k) => LOGISTICS_EXPORT_GATES[k].owner_type === ownerType);
/** `logistics_customs_export` — the customs-records register only (`/logistics/export/customs_records`). */
const LOGISTICS_CUSTOMS_OVERLAY_KINDS = [
    'customs_records',
];
/** `customs_tax_administration_export` — `CTA_OVERLAY_EXPORT_KINDS` in `guarded-paths.ts`. */
const CTA_OVERLAY_KINDS = [
    'duty_free_lists',
    'examinations',
    'transition_statement',
];
/** `manufacturing_export` — `MANUFACTURING_OVERLAY_EXPORT_KINDS` in `guarded-paths.ts`. */
const MANUFACTURING_OVERLAY_KINDS = [
    'trace_events',
    'epr_scopes',
    'epr_submissions',
    'substances',
    'compositions',
];
export const OVERLAY_ATTACHMENT_OWNER_TYPES = {
    // ── Overlay-exclusive registers (Class A) ──────────────────────────
    // Banking & Payments, Crypto Assets.
    credit_information_supply_export: ALWAYS,
    supervisory_reporting_export: ALWAYS,
    supervisory_reporting_evidence: ALWAYS,
    aml_case_evidence: ALWAYS,
    aml_programme_export: ALWAYS,
    ekyc_disposal_evidence: ALWAYS,
    payment_data_security_evidence: ALWAYS,
    payments_evidence_export: ALWAYS,
    pcf_supervision_evidence: ALWAYS,
    pcf_supervision_export: ALWAYS,
    // Cross-Sector.
    cybersecurity_assurance_index_evidence: ALWAYS,
    cybersecurity_assurance_index_export: ALWAYS,
    biometric_capture_evidence: ALWAYS,
    vehicle_monitoring_evidence: ALWAYS,
    vehicle_monitoring_export: ALWAYS,
    biometric_capture_export: ALWAYS,
    // Food Traceability. The export is the registers'; the evidence type is also
    // held against shared product and recall records (below).
    food_traceability_export: ALWAYS,
    food_traceability_evidence: {
        overlay: 'by_owner_record',
        shared_owner_collections: [MANUFACTURING_PRODUCT_COLLECTION, MANUFACTURING_CAPA_COLLECTION],
    },
    // Public Administration, Publishing, Education.
    issued_record_export: ALWAYS,
    publication_prerequisite_evidence: ALWAYS,
    public_data_sharing_agreement: ALWAYS,
    public_data_acknowledgement: ALWAYS,
    public_data_export: ALWAYS,
    // Digital Technology.
    platform_seller_registration_evidence: ALWAYS,
    platform_document_evidence: ALWAYS,
    platform_evidence_export: ALWAYS,
    regulatory_fee_line_export: ALWAYS,
    licence_fee_receipt: ALWAYS,
    sender_name_fee_receipt: ALWAYS,
    licence_fee_decision_document: ALWAYS,
    // Transport & Logistics.
    regulated_account_export: ALWAYS,
    logistics_customs_evidence: ALWAYS,
    logistics_claim_evidence: ALWAYS,
    logistics_authorisation_evidence: ALWAYS,
    logistics_claim_export: ALWAYS,
    logistics_authorisation_export: ALWAYS,
    logistics_customs_export: byKind(logisticsKindsOf('logistics_customs_export'), LOGISTICS_CUSTOMS_OVERLAY_KINDS),
    customs_tax_administration_export: byKind(CTA_EXPORT_KINDS, CTA_OVERLAY_KINDS),
    // Manufacturing. Trace events, EPR and the chemical register (substances and
    // each product's composition, which only the Chemical Register shows) are
    // Class A; conformity, CAPA/recall and OT remote access are shared.
    manufacturing_trace_evidence: ALWAYS,
    manufacturing_epr_fulfilment_evidence: ALWAYS,
    manufacturing_epr_submission_proof: ALWAYS,
    manufacturing_composition_evidence: ALWAYS,
    manufacturing_export: byKind(MANUFACTURING_EXPORT_KINDS, MANUFACTURING_OVERLAY_KINDS),
    // ── Shared, baseline and core registers ────────────────────────────
    // Manufacturing and logistics registers that stay in core.
    manufacturing_conformity_evidence: NONE,
    manufacturing_remote_access_evidence: NONE,
    logistics_shipment_document: NONE,
    logistics_clearance_evidence: NONE,
    logistics_delivery_proof: NONE,
    logistics_parcel_event_evidence: NONE,
    logistics_export: NONE,
    logistics_parcel_export: NONE,
    logistics_delivery_proof_export: NONE,
    customs_tax_administration_evidence: NONE,
    // Routers 63.42.0 leaves unguarded (controlled testing, data exploitation,
    // data services, authority directions and legal holds, marketing consents).
    controlled_testing_personnel_evidence: NONE,
    controlled_testing_solution_evidence: NONE,
    controlled_testing_child_results_report: NONE,
    controlled_testing_registry_evidence: NONE,
    controlled_testing_stop_evidence: NONE,
    controlled_testing_completion_evidence: NONE,
    data_exploitation_evidence: NONE,
    data_exploitation_export: NONE,
    data_service_provider_export: NONE,
    authority_direction_export: NONE,
    legal_hold_export: NONE,
    marketing_consent_evidence: NONE,
    marketing_suppression_evidence: NONE,
    dossier_authority_communication: NONE,
    dossier_authority_communication_export: NONE,
    org_profile_statement: NONE,
    // Core modules.
    governance_evidence: NONE,
    playbook_execution_step: NONE,
    offboarding_data_return: NONE,
    approval_decision: NONE,
    vendor_questionnaire_answer: NONE,
    vendor_onboarding_case: NONE,
    rights_request_attachment: NONE,
    data_mapping_evidence: NONE,
    deletion_step_execution: NONE,
    incident_evidence: NONE,
    monitoring_task_instance: NONE,
    dpia_record: NONE,
    transfer_assessment_record: NONE,
    form_instance_response: NONE,
    consent_workflow: NONE,
    incident_ops_evidence: NONE,
    governance_cross_border_export: NONE,
    governance_board_report_export: NONE,
    governance_evidence_bundle_export: NONE,
    case_evidence_pack_export: NONE,
    framework_evidence_pack_export: NONE,
    framework_evidence_pack_hard_copy_proof: NONE,
    // Sprint 62.58.0 Slice C — a framework's briefing documents and its summary
    // export (Localization, core, under `regulatory_framework:read`).
    framework_briefing: NONE,
    framework_summary_export: NONE,
    // Sprint 62.57.0 Slice E (D44) — the processing-service certificate register
    // is a PDPL capability, outside every Sector Overlays guarded prefix.
    service_certificate_document: NONE,
    service_certificate_application_document: NONE,
    service_certificate_communication: NONE,
    service_certificate_export: NONE,
    service_certificate_return: NONE,
    service_certificate_supplement_sent: NONE,
    // §12.13 — the vendor register is core governance, outside every guarded prefix.
    vendor_service_certificate_evidence: NONE,
    // Sprint 62.58.0 Slice N — a deployment access statement's documents (Data
    // Mapping, core, under `dm:activity:read`).
    access_statement: NONE,
    pdpl_evidence_pack_dpia_export: NONE,
    pdpl_evidence_pack_otia_export: NONE,
    incident_report_export: NONE,
    vendor_onboarding_export: NONE,
    forseti_artifact: NONE,
    data_mapping_export: NONE,
    classification_export: NONE,
    penalty_exposure_export: NONE,
    enforcement_case_export: NONE,
    scheduled_export_run: NONE,
    compliance_obligation_export: NONE,
    audit_evidence_pack_submission: NONE,
    incident_reports_list_export: NONE,
    rights_request_response_export: NONE,
    audit_events_export: NONE,
    consent_audit_export: NONE,
    discovery_report_export: NONE,
    form_filing_case_links_export: NONE,
    org_group_audit_export: NONE,
    access_accountability_export: NONE,
    consent_evidence_pack_export: NONE,
    ai_governance_evidence: NONE,
    ai_governance_authority_export: NONE,
    client_csv_export: NONE,
    form_output_dpia: NONE,
    form_output_transfer_assessment: NONE,
    form_output_dossier: NONE,
    form_output_incident_report: NONE,
    form_output_breach_notification: NONE,
    form_output_authority_request: NONE,
    form_output_cross_border_filing: NONE,
    form_output_other: NONE,
    form_output_restricted_register: NONE,
    submission_package_document: NONE,
    vendor_contract: NONE,
    vendor_soc_report: NONE,
    vendor_dpa: NONE,
    vendor_nda: NONE,
    vendor_termination_notice: NONE,
    vendor_amendment: NONE,
    rights_request_case_file: NONE,
    rights_request_evidence_pack: NONE,
    rights_request_bulk_export: NONE,
    vendor_risk_acceptance_export: NONE,
    vendor_subprocessor_register_export: NONE,
    vendor_questionnaire_export: NONE,
    vendor_questionnaire_template_export: NONE,
    governance_model_export: NONE,
    audit_readiness_export: NONE,
    document_change_log_export: NONE,
    governance_documents_export: NONE,
    document_issuance_export: NONE,
    maturity_models_export: NONE,
    maturity_walkthrough_export: NONE,
    audit_walkthrough_export: NONE,
    remediation_plans_export: NONE,
    reporting_periods_export: NONE,
    trend_metrics_export: NONE,
    acknowledgment_export: NONE,
    doc_drafting_export: NONE,
    program_health_export: NONE,
    raci_matrix_export: NONE,
    monitoring_authority_readiness_export: NONE,
    monitoring_change_reviews_export: NONE,
    monitoring_framework_links_export: NONE,
    monitoring_kpi_export: NONE,
    monitoring_periods_export: NONE,
    monitoring_programs_export: NONE,
    monitoring_ric_cases_export: NONE,
    monitoring_task_templates_export: NONE,
    monitoring_tasks_export: NONE,
    consent_property_binding_export: NONE,
    consent_property_registry_export: NONE,
    consent_property_change_requests_export: NONE,
    discovery_classification_rules_export: NONE,
    discovery_dedup_rules_export: NONE,
    discovery_import_templates_export: NONE,
    discovery_connectors_export: NONE,
    discovery_scans_export: NONE,
    discovery_staging_export: NONE,
    discovery_suggestions_export: NONE,
    discovery_analytics_export: NONE,
    connector_automation_programs_export: NONE,
    connector_system_inventory_export: NONE,
    connector_traceability_matrix_export: NONE,
    connector_gap_register_export: NONE,
    connector_brds_export: NONE,
    connector_decision_records_export: NONE,
    connector_control_mappings_export: NONE,
    connector_instances_export: NONE,
    connector_definitions_export: NONE,
    connector_event_bus_export: NONE,
    connector_poc_scorecards_export: NONE,
    connector_security_reviews_export: NONE,
    connector_credentials_export: NONE,
    deletion_execution_tasks_export: NONE,
    deletion_vendor_requests_export: NONE,
    deletion_proof_chain_export: NONE,
    incident_ops_playbook_templates_export: NONE,
    incident_ops_regulator_exports_export: NONE,
    incident_ops_response_metrics_export: NONE,
    incident_ops_sla_dashboard_export: NONE,
    incident_ops_sla_definitions_export: NONE,
    incident_ops_vendor_coordination_export: NONE,
    access_session_events_export: NONE,
    access_anchoring_proofs_export: NONE,
    access_justifications_export: NONE,
    access_investigations_list_export: NONE,
    access_investigation_case_export: NONE,
    access_log_list_export: NONE,
    access_policies_export: NONE,
    access_review_campaigns_export: NONE,
    access_evidence_packages_export: NONE,
    access_evidence_package_detail_export: NONE,
    organizations_export: NONE,
    organization_intake_configs_export: NONE,
    organization_departments_export: NONE,
    organization_framework_assignments_export: NONE,
    organization_compliance_rollup_export: NONE,
    org_groups_list_export: NONE,
    org_group_dashboard_export: NONE,
    org_group_reports_export: NONE,
    org_group_consolidated_audit_export: NONE,
    org_group_per_org_audit_export: NONE,
    org_group_users_export: NONE,
    org_group_shared_vendors_export: NONE,
    org_group_shared_frameworks_export: NONE,
    org_group_shared_form_templates_export: NONE,
    org_group_merged_vendors_export: NONE,
    partner_orgs_export: NONE,
    partner_org_detail_export: NONE,
    partner_dashboard_export: NONE,
    partner_reports_export: NONE,
    partner_audit_list_export: NONE,
    partner_workflow_templates_export: NONE,
    partner_workflow_instances_export: NONE,
    partner_deliverables_export: NONE,
    partner_deliverable_templates_export: NONE,
    partner_support_tickets_export: NONE,
    partner_ticket_detail_export: NONE,
    partner_ticket_attachment: NONE,
    partner_escalation_dashboard_export: NONE,
    partner_service_tiers_export: NONE,
    partner_brand_profiles_export: NONE,
    task_list_export: NONE,
    task_board_export: NONE,
    task_detail_export: NONE,
    my_tasks_export: NONE,
    overdue_tasks_export: NONE,
    task_calendar_export: NONE,
    workflow_list_export: NONE,
    workflow_detail_export: NONE,
    workflow_template_catalog_export: NONE,
    task_conflict_detection_export: NONE,
    task_consolidation_export: NONE,
    task_section_assignments_export: NONE,
    form_output_print_export: NONE,
    cover_letter_dpia_dossier: NONE,
    cover_letter_mps_submission: NONE,
    pdpl_email_send_proof_2026: NONE,
    pdpl_authority_response_evidence_2026: NONE,
    pdpl_hard_copy_receipt_2026: NONE,
    regulatory_artifacts_export: NONE,
};
/** The class of `ownerType`; an owner-type string nobody declared is `none`. */
export function overlayAttachmentOwnerClassOf(ownerType) {
    if (typeof ownerType !== 'string')
        return NONE;
    return Object.prototype.hasOwnProperty.call(OVERLAY_ATTACHMENT_OWNER_TYPES, ownerType)
        ? OVERLAY_ATTACHMENT_OWNER_TYPES[ownerType]
        : NONE;
}
/** Every owner type whose files can be overlay content (any class but `none`). */
export const OVERLAY_CANDIDATE_OWNER_TYPES = Object.keys(OVERLAY_ATTACHMENT_OWNER_TYPES).filter((t) => OVERLAY_ATTACHMENT_OWNER_TYPES[t].overlay !== 'none');
//# sourceMappingURL=overlay-attachment-owner-types.js.map